import "./index.scss";

import { Box, Grid, Modal, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import Input from "components/Input";
import ScreenLoader from "components/ScreenLoader";
import SearchablePile from "components/SearchablePile";
import SearchableTreeList, { TSearchableTreeNodeItem } from "components/SearchableTreeList";
import { MSG_OPERATION_FAILED, MSG_OPERATION_SUCCESSFUL } from "constants/index";
import FormWrapper from "containers/Members/components/FormWrapper";
import _ from "lodash";
import { Circuit } from "models/circuit";
import {
  addCircuitEntry,
  getCircuitById,
  updateCircuitEntry,
} from "modules/circuit/circuitServices";
import { getLkpExercisesByMuscleGroup } from "modules/lookup/lookupServices";
import { memo, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

const CircuitCreateEditModal = (props: { open: boolean; onClose: any; entryId?: number }) => {
  const { open, onClose, entryId } = props;
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pileOptions, setPileOptions] = useState([]);
  const [lookupsLoadded, setLookupsLoadded] = useState(false);
  const [exerciseByMuscleGroupOptions, setExerciseByMuscleGroupOptions] = useState<
    TSearchableTreeNodeItem[]
  >([]);
  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);

  const methods = useForm<Circuit>({
    defaultValues: {
      description: "",
      exercises: [],
      id: 0,
      title: "",
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    control,
    reset,
  } = methods;

  useEffect(() => {
    if (open) {
      setLoading(true);

      let promises: Promise<any>[] = [];

      if (!lookupsLoadded) {
        promises.push(
          getLkpExercisesByMuscleGroup({ limit: 0 }).then((res) => {
            if (res.success) {
              let tempArray = [];
              Object.keys(res.data).forEach((key) => {
                let entries = [];
                res.data[key].forEach((exercise) => {
                  entries.push({ label: exercise.exercise_title, value: exercise.exercise_id });
                });
                tempArray.push({
                  label: key,
                  subItems: entries,
                });
              });
              setExerciseByMuscleGroupOptions(tempArray);
            }
          })
        );
      }

      if (entryId) {
        promises.push(
          getCircuitById({ id: entryId }).then((r) => {
            if (r.success) {
              const fetchedEntry = r.data;
              setValue("id", entryId);
              setValue("title", fetchedEntry.title);
              setValue("description", fetchedEntry.description);
              setValue("exercises", fetchedEntry.exercises);
              setPileOptions(fetchedEntry.exercises.map((m) => ({ label: m.title, value: m.id })));
            }
          })
        );
      }

      Promise.all(promises).then((_v) => {
        setLoading(false);
        setLookupsLoadded(true);
      });
    }
  }, [entryId, open]);

  useEffect(() => {
    setValue(
      "exercises",
      pileOptions.map((e) => e.value)
    );
  }, [pileOptions]);

  //#region event handlers
  const handleCloseModal = useCallback((isRefreshNeeded = false) => {
    if (isRefreshNeeded === true) onClose(isRefreshNeeded);
    else setShowDataWillBeLostModal(true);
  }, []);

  const onCloseConfirm = (discardChanges = false) => {
    if (discardChanges) {
      reset({
        exercises: [],
        id: 0,
        title: "",
      });
      setShowDataWillBeLostModal(false);
      onClose();
    } else {
      setShowDataWillBeLostModal(false);
    }
  };

  const handleConfirm = (data) => {
    setIsSaving(true);
    const createOrUpdateAPI = entryId ? updateCircuitEntry : addCircuitEntry;
    createOrUpdateAPI(data)
      .then((r) => {
        if (r.success) {
          toast(MSG_OPERATION_SUCCESSFUL, { type: "success" });
          handleCloseModal(true);
        }
      })
      .catch((e) => {
        console.log(e);
        toast(MSG_OPERATION_FAILED, { type: "error" });
      })
      .finally(() => setIsSaving(false));
  };

  const handleAddExerciseToPile = (item) => {
    const indexInPile = pileOptions.findIndex((e) => e.value === item.value);
    if (indexInPile === -1) {
      setPileOptions([...pileOptions, item]);
    }
  };

  const handleRemoveExerciseFromPile = (item) => {
    const indexInPile = pileOptions.findIndex((e) => e.value === item.value);
    if (indexInPile !== -1) {
      let arr = [...pileOptions];
      arr.splice(indexInPile, 1);
      setPileOptions([...arr]);
    }
  };
  //#endregion event handlers

  return (
    <>
      <Modal
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        open={open}
        onClose={handleCloseModal}
      >
        <Stack className="personal-details-modal-container circuit-create-edit-modal">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="40px"
            className="header"
          >
            <h1>{entryId ? `Edit` : `Create`} Circuit</h1>
            <DeleteIcon width="24px" height="24px" onClick={() => handleCloseModal()} />
          </Stack>
          {loading ? (
            <ScreenLoader isScreenLoader />
          ) : (
            <form onSubmit={handleSubmit(handleConfirm)} className="manage-protocol-container">
              <Box>
                <FormWrapper label="Circuit Name" isRequired>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Input
                          {...field}
                          hasError={!_.isEmpty(errors.title)}
                          placeholder="Please enter"
                          maxLength={25}
                        />
                        {errors.title?.type === "required" && (
                          <h4 className="error">This field is required</h4>
                        )}
                      </Stack>
                    )}
                  />
                </FormWrapper>
              </Box>
              <Box>
                <FormWrapper label="Description">
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Input
                          {...field}
                          hasError={!_.isEmpty(errors.description)}
                          placeholder="Please enter"
                          maxLength={25}
                        />
                        {errors.description?.type === "required" && (
                          <h4 className="error">This field is required</h4>
                        )}
                      </Stack>
                    )}
                  />
                </FormWrapper>
              </Box>
              <Grid container columnSpacing={"20px"}>
                <Grid item xs={6}>
                  <Stack>
                    <h2 className="headerSpacing">Exercises</h2>
                    <SearchableTreeList
                      options={exerciseByMuscleGroupOptions}
                      callback={(item) => {
                        handleAddExerciseToPile(item);
                      }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack>
                    <h2 className="headerSpacing">Selected Exercises</h2>
                    <SearchablePile options={pileOptions} callback={handleRemoveExerciseFromPile} />
                  </Stack>
                </Grid>
              </Grid>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Button
                    label="Cancel"
                    className="text-button"
                    onClick={() => handleCloseModal()}
                    type="button"
                  />
                  <Button label="Save" className="confirm-button" loading={isSaving} />
                </Stack>
              </Stack>
            </form>
          )}
        </Stack>
      </Modal>
      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={onCloseConfirm}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
    </>
  );
};

export default memo(CircuitCreateEditModal);
